/**
 * UpdateManager - Gestor de actualizaciones con detección de cambios
 * Utiliza polling periódico para detectar nuevas versiones del SW
 */

export class UpdateManager {
  constructor(options = {}) {
    // Intervalo de polling (5 minutos por defecto)
    this.pollInterval = options.pollInterval || 300000;

    // Almacenar el hash del manifest para detectar cambios
    this.manifestHash = null;
    this.currentSWVersion = null;

    // Control de polling
    this.pollTimer = null;
    this.isPolling = false;

    // Registro del service worker
    this.registration = null;

    // Callbacks
    this.onUpdate = options.onUpdate || (() => {});
    this.onCheckComplete = options.onCheckComplete || (() => {});

    this.init();
  }

  async init() {
    if (!navigator.serviceWorker) {
      console.warn("Service Worker no soportado");
      return;
    }

    try {
      this.registration = await navigator.serviceWorker.getRegistration();
      this.startPolling();
    } catch (error) {
      console.error("Error inicializando UpdateManager:", error);
    }
  }

  /**
   * Inicia el polling periódico para detectar cambios
   */
  startPolling() {
    if (this.isPolling) return;

    this.isPolling = true;

    // Hacer la primera verificación de inmediato
    this.checkForUpdates();

    // Luego verificar periódicamente
    this.pollTimer = setInterval(() => {
      this.checkForUpdates();
    }, this.pollInterval);

    console.log(
      `[UpdateManager] Polling iniciado cada ${this.pollInterval / 1000}s`
    );
  }

  /**
   * Detiene el polling
   */
  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.isPolling = false;
      console.log("[UpdateManager] Polling detenido");
    }
  }

  /**
   * Verifica actualizaciones del Service Worker y del archivo version.json
   */
  async checkForUpdates() {
    // 1. Verificar archivo version.json (Más robusto contra caché)
    const versionResult = await this.checkVersionFile();
    const versionChanged = versionResult.changed;
    if (versionChanged) {
      console.log(
        "[UpdateManager] Cambio de versión detectado via version.json"
      );
      this.onUpdate({
        detail: {
          reason: "version-json-change",
          timestamp: versionResult.timestamp,
        },
        type: "version-mismatch",
      });
      // No retornamos aquí para permitir que el SW también intente actualizarse
    }

    if (!this.registration) {
      try {
        this.registration = await navigator.serviceWorker.getRegistration();
        if (!this.registration) return versionChanged;
      } catch (error) {
        console.error("Error obteniendo registration:", error);
        return versionChanged;
      }
    }

    try {
      // Forzar actualización del registro del SW
      await this.registration.update();

      // Verificar si hay un nuevo SW esperando
      if (this.registration.waiting) {
        console.log("[UpdateManager] Nueva versión de SW detectada");
        this.onUpdate({
          detail: this.registration,
          type: "sw-update",
        });
        return true;
      }

      // Método adicional: Verificar cambios en el manifest
      const manifestChanged = await this.checkManifestChanges();
      if (manifestChanged && !this.registration.waiting) {
        console.log(
          "[UpdateManager] Cambios detectados en manifest, forzando update"
        );
        await this.registration.update();
        if (this.registration.waiting) {
          this.onUpdate({
            detail: this.registration,
            type: "manifest-change",
          });
          return true;
        }
      }

      this.onCheckComplete({ hasUpdate: versionChanged });
      return versionChanged;
    } catch (error) {
      console.error("[UpdateManager] Error en checkForUpdates:", error);
      this.onCheckComplete({ hasUpdate: versionChanged, error });
      return versionChanged;
    }
  }

  /**
   * Verifica el archivo version.json en el servidor omitiendo el caché
   */
  async checkVersionFile() {
    try {
      const response = await fetch(`/version.json?t=${Date.now()}`, {
        cache: "no-store",
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      });

      if (!response.ok) return { changed: false };

      const data = await response.json();
      const lastVersionTimestamp = localStorage.getItem(
        "app_version_timestamp"
      );

      if (!lastVersionTimestamp) {
        // Primera vez, guardar y continuar
        localStorage.setItem("app_version_timestamp", data.timestamp);
        return { changed: false };
      }

      // Si el timestamp es mayor, hay una nueva versión
      const changed = parseInt(data.timestamp) > parseInt(lastVersionTimestamp);
      return { changed, timestamp: data.timestamp };
    } catch (error) {
      console.error("[UpdateManager] Error en checkVersionFile:", error);
      return { changed: false };
    }
  }

  /**
   * Determina si el usuario está en una ruta "crítica" donde no se debe recargar
   */
  isBusy() {
    const path = window.location.pathname;
    const busyPaths = ["/buy", "/transaction/request", "/admin/orders/"];
    return busyPaths.some((p) => path.includes(p));
  }

  /**
   * Verifica si el manifest cambió (detecta cambios de JS/CSS)
   * Útil como fallback cuando el SW no detecta cambios inmediatamente
   */
  async checkManifestChanges() {
    try {
      // Intentar obtener el manifest precache desde el SW
      const caches_list = await caches.keys();
      const precacheKey = caches_list.find((name) => name.includes("precache"));

      if (!precacheKey) return false;

      const cache = await caches.open(precacheKey);
      const manifestResponse = await cache.match("/index.html");

      if (!manifestResponse) return false;

      const manifestText = await manifestResponse.text();
      const newHash = this.hashString(manifestText);

      if (this.manifestHash === null) {
        // Primera vez, solo guardar el hash
        this.manifestHash = newHash;
        return false;
      }

      const changed = this.manifestHash !== newHash;
      if (changed) {
        console.log(
          "[UpdateManager] Cambios detectados en archivos del manifest"
        );
        this.manifestHash = newHash;
      }

      return changed;
    } catch (error) {
      console.error("[UpdateManager] Error en checkManifestChanges:", error);
      return false;
    }
  }

  /**
   * Simple hash function para comparar contenido
   */
  hashString(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convertir a entero 32-bit
    }
    return hash.toString();
  }

  /**
   * Aplica la actualización de inmediato
   */
  applyUpdate() {
    if (!this.registration?.waiting) {
      console.warn("[UpdateManager] No hay SW esperando para actualizar");
      return false;
    }

    try {
      this.registration.waiting.postMessage({ type: "SKIP_WAITING" });
      console.log("[UpdateManager] Update aplicado");
      return true;
    } catch (error) {
      console.error("[UpdateManager] Error aplicando update:", error);
      return false;
    }
  }

  /**
   * Destruye el manager y limpia recursos
   */
  destroy() {
    this.stopPolling();
    this.registration = null;
    this.onUpdate = null;
    this.onCheckComplete = null;
  }
}

// Singleton global
let updateManagerInstance = null;

export function getUpdateManager(options = {}) {
  if (!updateManagerInstance) {
    updateManagerInstance = new UpdateManager(options);
  }
  return updateManagerInstance;
}

export function destroyUpdateManager() {
  if (updateManagerInstance) {
    updateManagerInstance.destroy();
    updateManagerInstance = null;
  }
}
